import { 
  ParsedABI, 
  ContractMethod, 
  ContractEvent, 
  ContractError, 
  ContractConstructor,
  ContractReceive,
  ContractFallback 
} from '../types';

/**
 * Advanced ABI parser class for full support of all ABI types
 * Automatically detects all functions, events, errors etc. based on standard ABI structure
 */
export class ABIParser {
  /**
   * Parses a complete ABI and categorizes all elements
   * @param abi - The contract ABI array
   * @returns ParsedABI - Categorized ABI elements
   */
  static parseABI(abi: any[]): ParsedABI {
    const parsed: ParsedABI = {
      functions: [],
      events: [],
      errors: [],
      constructor: undefined,
      receive: undefined,
      fallback: undefined
    };

    for (const item of abi) {
      switch (item.type) {
        case 'function':
          parsed.functions.push(this.parseFunction(item));
          break;
        case 'event':
          parsed.events.push(this.parseEvent(item));
          break;
        case 'error':
          parsed.errors.push(this.parseError(item));
          break;
        case 'constructor':
          parsed.constructor = this.parseConstructor(item);
          break;
        case 'receive':
          parsed.receive = this.parseReceive(item);
          break;
        case 'fallback':
          parsed.fallback = this.parseFallback(item);
          break;
        default:
          // Skip unknown ABI item types silently
      }
    }

    return parsed;
  }

  /**
   * Extrahiert nur Funktionen aus der ABI (bestehende Logik)
   * @param abi - The contract ABI array
   * @returns ContractMethod[] - Array aller Funktionen
   */
  static extractFunctions(abi: any[]): ContractMethod[] {
    return abi
      .filter(item => item.type === 'function')
      .map(item => this.parseFunction(item));
  }

  /**
   * Extrahiert nur Events aus der ABI
   * @param abi - The contract ABI array
   * @returns ContractEvent[] - Array aller Events
   */
  static extractEvents(abi: any[]): ContractEvent[] {
    return abi
      .filter(item => item.type === 'event')
      .map(item => this.parseEvent(item));
  }

  /**
   * Extrahiert nur Errors aus der ABI
   * @param abi - The contract ABI array
   * @returns ContractError[] - Array aller Errors
   */
  static extractErrors(abi: any[]): ContractError[] {
    return abi
      .filter(item => item.type === 'error')
      .map(item => this.parseError(item));
  }

  /**
   * Findet eine spezifische Funktion in der ABI
   * @param abi - The contract ABI array
   * @param functionName - Name der gesuchten Funktion
   * @returns ContractMethod | undefined
   */
  static findFunction(abi: any[], functionName: string): ContractMethod | undefined {
    const functions = this.extractFunctions(abi);
    return functions.find(func => func.name === functionName);
  }

  /**
   * Kategorisiert Funktionen nach stateMutability
   * @param abi - The contract ABI array
   * @returns Objekt mit kategorisierten Funktionen
   */
  static categorizeFunctions(abi: any[]): {
    view: ContractMethod[];
    pure: ContractMethod[];
    nonpayable: ContractMethod[];
    payable: ContractMethod[];
  } {
    const functions = this.extractFunctions(abi);
    
    return {
      view: functions.filter(f => f.stateMutability === 'view'),
      pure: functions.filter(f => f.stateMutability === 'pure'),
      nonpayable: functions.filter(f => f.stateMutability === 'nonpayable'),
      payable: functions.filter(f => f.stateMutability === 'payable')
    };
  }

  /**
   * Creates a summary of the ABI
   * @param abi - The contract ABI array
   * @returns Statistical information about the ABI
   */
  static getABISummary(abi: any[]): {
    totalFunctions: number;
    totalEvents: number;
    totalErrors: number;
    hasConstructor: boolean;
    hasReceive: boolean;
    hasFallback: boolean;
    functionsByType: {
      view: number;
      pure: number;
      nonpayable: number;
      payable: number;
    };
  } {
    const parsed = this.parseABI(abi);
    const categorized = this.categorizeFunctions(abi);

    return {
      totalFunctions: parsed.functions.length,
      totalEvents: parsed.events.length,
      totalErrors: parsed.errors.length,
      hasConstructor: !!parsed.constructor,
      hasReceive: !!parsed.receive,
      hasFallback: !!parsed.fallback,
      functionsByType: {
        view: categorized.view.length,
        pure: categorized.pure.length,
        nonpayable: categorized.nonpayable.length,
        payable: categorized.payable.length
      }
    };
  }

  /**
   * Validates an ABI for completeness and correctness
   * @param abi - The contract ABI array
   * @returns Array of validation errors (empty if valid)
   */
  static validateABI(abi: any[]): string[] {
    const errors: string[] = [];

    if (!Array.isArray(abi)) {
      errors.push('ABI must be an array');
      return errors;
    }

    for (let i = 0; i < abi.length; i++) {
      const item = abi[i];
      
      if (!item.type) {
        errors.push(`Item at index ${i} is missing 'type' field`);
        continue;
      }

      switch (item.type) {
        case 'function':
          if (!item.name) errors.push(`Function at index ${i} is missing 'name' field`);
          if (!item.inputs) errors.push(`Function at index ${i} is missing 'inputs' field`);
          if (!item.outputs) errors.push(`Function at index ${i} is missing 'outputs' field`);
          break;
        case 'event':
          if (!item.name) errors.push(`Event at index ${i} is missing 'name' field`);
          if (!item.inputs) errors.push(`Event at index ${i} is missing 'inputs' field`);
          break;
        case 'error':
          if (!item.name) errors.push(`Error at index ${i} is missing 'name' field`);
          break;
        case 'constructor':
        case 'receive':
        case 'fallback':
          // These have special validation rules but are generally optional
          break;
        default:
          errors.push(`Unknown type '${item.type}' at index ${i}`);
      }
    }

    return errors;
  }

  // Private Helper Methods
  private static parseFunction(item: any): ContractMethod {
    return {
      name: item.name,
      inputs: item.inputs || [],
      outputs: item.outputs || [],
      stateMutability: item.stateMutability || 'nonpayable',
      type: 'function'
    };
  }

  private static parseEvent(item: any): ContractEvent {
    return {
      name: item.name,
      inputs: item.inputs || [],
      anonymous: item.anonymous || false,
      type: 'event'
    };
  }

  private static parseError(item: any): ContractError {
    return {
      name: item.name,
      inputs: item.inputs || [],
      type: 'error'
    };
  }

  private static parseConstructor(item: any): ContractConstructor {
    return {
      inputs: item.inputs || [],
      stateMutability: item.stateMutability || 'nonpayable',
      type: 'constructor'
    };
  }

  private static parseReceive(_item: any): ContractReceive {
    return {
      stateMutability: 'payable',
      type: 'receive'
    };
  }

  private static parseFallback(item: any): ContractFallback {
    return {
      stateMutability: item.stateMutability || 'nonpayable',
      type: 'fallback'
    };
  }
}

/**
 * Convenience function: Extrahiert alle Funktionen aus einer ABI
 * @param abi - Die Contract ABI Array
 * @returns ContractMethod[] - Array aller erkannten Funktionen
 */
export function extractAllFunctions(abi: any[]): ContractMethod[] {
  return ABIParser.extractFunctions(abi);
}

/**
 * Convenience function: Parses an ABI completely
 * @param abi - The contract ABI array
 * @returns ParsedABI - Fully categorized ABI
 */
export function parseContractABI(abi: any[]): ParsedABI {
  return ABIParser.parseABI(abi);
}

/**
 * Convenience function: Creates ABI summary
 * @param abi - The contract ABI array
 * @returns ABI statistics
 */
export function getABIInfo(abi: any[]) {
  return ABIParser.getABISummary(abi);
}
